import {
    CheckCircleRounded,
    ExpandLess,
    ExpandMore,
    RadioButtonUncheckedOutlined,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Popover,
    Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

type Option = {
    label: string;
    value: string;
    disabled?: boolean;
};

type SectionProps = {
    options: Option[];
    header?: string;
    description?: string;
};

export type FilterRecord = Record<string, string[]>;

type Props = {
    title: string;
    sections: SectionProps[];
    setFilter: Dispatch<SetStateAction<Record<string, string[]>>>;
    defaultValue?: string[];
    filterKey: string;
    hideValues?: boolean;
};

export const Filter = ({
    title,
    sections,
    setFilter,
    filterKey,
    defaultValue,
    hideValues,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [filters, setFilters] = useState<string[]>(defaultValue || []);

    const { t } = useTranslation();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const allValues = sections.flatMap((section) =>
        section.options
            .filter((option) => !option.disabled)
            .map((option) => ({ value: option.value, label: option.label }))
    );

    const activateFilter = () => {
        handleClose();
        setFilter((prev) => ({
            ...prev,
            [filterKey]: filters,
        }));
    };

    const handleOptionClick = (optionValue: string) => {
        setFilters((prev) => {
            const newFilters = [...prev];
            if (newFilters.includes(optionValue)) {
                const index = newFilters.indexOf(optionValue);
                newFilters.splice(index, 1);
            } else {
                newFilters.push(optionValue);
            }

            return newFilters;
        });
    };

    const handleSelectAll = () => {
        setFilters(allValues.map((option) => option.value));
    };

    const handleReset = () => {
        setFilters(defaultValue || []);
        setFilter((prev) => ({ ...prev, [filterKey]: defaultValue || [] }));
    };

    const filterDisplay =
        !hideValues && filters.length
            ? filters.length === allValues.length
                ? t("common.all", "All")
                : filters
                      .map(
                          (value) =>
                              allValues.find((option) => option.value === value)
                                  ?.label
                      )
                      .join(", ")
            : t("common.none", "None");

    return (
        <>
            <Button
                variant="outlined"
                onClick={handleClick}
                sx={{
                    padding: 3,
                    borderColor: "divider",
                    width: "100%",
                    justifyContent: "space-between",
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    {title}
                    {!hideValues && (
                        <>
                            :
                            <Typography variant="faded" fontSize={16}>
                                {filterDisplay}
                            </Typography>
                        </>
                    )}
                </Box>
                {open ? (
                    <ExpandLess fontSize="inherit" />
                ) : (
                    <ExpandMore fontSize="inherit" />
                )}
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            maxWidth: 550,
                        },
                    },
                }}
            >
                <Box sx={{ paddingY: 4, paddingX: 6 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 4,
                            alignItems: "center",
                        }}
                    >
                        <Typography fontWeight="bold">{title}</Typography>
                        <Button variant="underlined" onClick={handleSelectAll}>
                            Select all
                        </Button>
                    </Box>
                    {sections.map(
                        ({ header, description, options }, sectionIndex) => (
                            <Box key={`section-${sectionIndex}`}>
                                {header && (
                                    <Box marginBottom={4}>
                                        <Typography
                                            fontWeight="bold"
                                            fontSize={14}
                                        >
                                            {header}
                                        </Typography>
                                        {description && (
                                            <Typography fontSize={12}>
                                                {description}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                <FormGroup
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        flexDirection: "row",
                                    }}
                                >
                                    {options.map((option, optionIndex) => (
                                        <FormControlLabel
                                            key={`option-${option.value}-${optionIndex}`}
                                            sx={{
                                                border: 1,
                                                borderColor: "divider",
                                                borderRadius: 100,
                                                margin: 0,
                                                padding: 2,
                                                paddingRight: 3,
                                                gap: 2,
                                            }}
                                            disabled={option.disabled}
                                            label={option.label}
                                            slotProps={{
                                                typography: {
                                                    fontSize: 14,
                                                },
                                            }}
                                            control={
                                                <Checkbox
                                                    sx={{ padding: 0 }}
                                                    value={option.value}
                                                    checked={filters.includes(
                                                        option.value
                                                    )}
                                                    onChange={() =>
                                                        handleOptionClick(
                                                            option.value
                                                        )
                                                    }
                                                    icon={
                                                        <RadioButtonUncheckedOutlined
                                                            sx={{
                                                                fontSize: 18,
                                                                color: option.disabled
                                                                    ? "divider"
                                                                    : "primary",
                                                            }}
                                                        />
                                                    }
                                                    checkedIcon={
                                                        <CheckCircleRounded
                                                            sx={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    }
                                                />
                                            }
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        )
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 3,
                            justifyContent: "end",
                            marginTop: 4,
                        }}
                    >
                        <Button variant="underlined" onClick={handleReset}>
                            {t("common.resetFilter", "Reset filter")}
                        </Button>
                        <Button variant="outlined" onClick={activateFilter}>
                            {t("common.applyFilter", "Apply filter")}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};
